import { DifficultyLevel } from '../interfaces/question.interface';
import { Pt, SymmetryShape } from '../interfaces/symmetry.interface';

/**
 * Shape bank for the Mirror Match (lines-of-symmetry) game — Grade-4
 * geometry. A spread of axis counts so the same session moves from the
 * tricky one-axis shapes (a single correct answer) up to the friendly
 * many-axis regular polygons.
 *
 * Points are authored around the origin; the stage centres them. Angles
 * in `axisAngles` are undirected (0 = horizontal) and only used to draw
 * the optional hint ghosts — the real check is reflection-based.
 */

/** Vertices of a regular n-gon, first vertex pointing up. */
function regularPolygon(n: number, r: number, rotationDeg = -90): Pt[] {
  const pts: Pt[] = [];
  const rot = (rotationDeg * Math.PI) / 180;
  for (let i = 0; i < n; i++) {
    const a = rot + (i * 2 * Math.PI) / n;
    pts.push({ x: +(r * Math.cos(a)).toFixed(2), y: +(r * Math.sin(a)).toFixed(2) });
  }
  return pts;
}

/** Vertices of an n-pointed star, first point up. */
function starPolygon(points: number, rOuter: number, rInner: number, rotationDeg = -90): Pt[] {
  const pts: Pt[] = [];
  const rot = (rotationDeg * Math.PI) / 180;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? rOuter : rInner;
    const a = rot + (i * Math.PI) / points;
    pts.push({ x: +(r * Math.cos(a)).toFixed(2), y: +(r * Math.sin(a)).toFixed(2) });
  }
  return pts;
}

/** The n axes of a regular n-gon/star with one axis vertical. */
function regularAxes(n: number): number[] {
  return Array.from({ length: n }, (_, i) => (90 + (i * 180) / n) % 180);
}

const D = DifficultyLevel;

export const SYMMETRY_SHAPES: SymmetryShape[] = [
  // ── One axis (hardest: a single correct mirror line) ──────────────
  {
    id: 'isosceles',
    name: { en: 'Isosceles Triangle', ar: 'مثلث متساوي الساقين' },
    points: [{ x: 0, y: -155 }, { x: 115, y: 120 }, { x: -115, y: 120 }],
    axisCount: 1, axisAngles: [90], difficulty: D.HARD
  },
  {
    id: 'kite',
    name: { en: 'Kite', ar: 'طائرة ورقية' },
    points: [{ x: 0, y: -165 }, { x: 100, y: -25 }, { x: 0, y: 160 }, { x: -100, y: -25 }],
    axisCount: 1, axisAngles: [90], difficulty: D.HARD
  },
  {
    id: 'chevron',
    name: { en: 'Chevron', ar: 'سهم' },
    // Symmetric about the horizontal axis (y = 0).
    points: [
      { x: -85, y: -125 }, { x: 40, y: -125 }, { x: 170, y: 0 },
      { x: 40, y: 125 }, { x: -85, y: 125 }, { x: 40, y: 0 }
    ],
    axisCount: 1, axisAngles: [0], difficulty: D.HARD
  },

  // ── Two axes ──────────────────────────────────────────────────────
  {
    id: 'rectangle',
    name: { en: 'Rectangle', ar: 'مستطيل' },
    points: [{ x: -155, y: -95 }, { x: 155, y: -95 }, { x: 155, y: 95 }, { x: -155, y: 95 }],
    axisCount: 2, axisAngles: [0, 90], difficulty: D.MEDIUM
  },
  {
    id: 'rhombus',
    name: { en: 'Rhombus', ar: 'معيّن' },
    points: [{ x: 0, y: -165 }, { x: 115, y: 0 }, { x: 0, y: 165 }, { x: -115, y: 0 }],
    axisCount: 2, axisAngles: [0, 90], difficulty: D.MEDIUM
  },

  // ── Many axes (friendliest: several correct answers) ──────────────
  {
    id: 'triangle-eq',
    name: { en: 'Equilateral Triangle', ar: 'مثلث متساوي الأضلاع' },
    points: regularPolygon(3, 160),
    axisCount: 3, axisAngles: regularAxes(3), difficulty: D.EASY
  },
  {
    id: 'square',
    name: { en: 'Square', ar: 'مربع' },
    points: regularPolygon(4, 160, -45), // upright square (flat sides)
    axisCount: 4, axisAngles: regularAxes(4), difficulty: D.EASY
  },
  {
    id: 'plus',
    name: { en: 'Plus', ar: 'علامة زائد' },
    points: [
      { x: -55, y: -160 }, { x: 55, y: -160 }, { x: 55, y: -55 }, { x: 160, y: -55 },
      { x: 160, y: 55 }, { x: 55, y: 55 }, { x: 55, y: 160 }, { x: -55, y: 160 },
      { x: -55, y: 55 }, { x: -160, y: 55 }, { x: -160, y: -55 }, { x: -55, y: -55 }
    ],
    axisCount: 4, axisAngles: [0, 45, 90, 135], difficulty: D.EASY
  },
  {
    id: 'pentagon',
    name: { en: 'Pentagon', ar: 'خماسي' },
    points: regularPolygon(5, 160),
    axisCount: 5, axisAngles: regularAxes(5), difficulty: D.EASY
  },
  {
    id: 'hexagon',
    name: { en: 'Hexagon', ar: 'سداسي' },
    points: regularPolygon(6, 160),
    axisCount: 6, axisAngles: regularAxes(6), difficulty: D.EASY
  },
  {
    id: 'star',
    name: { en: 'Star', ar: 'نجمة' },
    points: starPolygon(5, 165, 68),
    axisCount: 5, axisAngles: regularAxes(5), difficulty: D.EASY
  },

  // ── More one-axis shapes ──────────────────────────────────────────
  {
    id: 'trapezoid',
    name: { en: 'Trapezoid', ar: 'شبه منحرف' },
    points: [{ x: -80, y: -95 }, { x: 80, y: -95 }, { x: 145, y: 110 }, { x: -145, y: 110 }],
    axisCount: 1, axisAngles: [90], difficulty: D.HARD
  },
  {
    id: 'arrow-up',
    name: { en: 'Arrow', ar: 'سهم لأعلى' },
    points: [
      { x: 0, y: -160 }, { x: 110, y: -40 }, { x: 45, y: -40 }, { x: 45, y: 150 },
      { x: -45, y: 150 }, { x: -45, y: -40 }, { x: -110, y: -40 }
    ],
    axisCount: 1, axisAngles: [90], difficulty: D.MEDIUM
  },
  {
    id: 'letter-t',
    name: { en: 'Letter T', ar: 'حرف T' },
    points: [
      { x: -140, y: -140 }, { x: 140, y: -140 }, { x: 140, y: -60 }, { x: 45, y: -60 },
      { x: 45, y: 150 }, { x: -45, y: 150 }, { x: -45, y: -60 }, { x: -140, y: -60 }
    ],
    axisCount: 1, axisAngles: [90], difficulty: D.HARD
  },
  {
    id: 'house',
    name: { en: 'House', ar: 'منزل' },
    points: [{ x: 0, y: -160 }, { x: 130, y: -40 }, { x: 130, y: 150 }, { x: -130, y: 150 }, { x: -130, y: -40 }],
    axisCount: 1, axisAngles: [90], difficulty: D.HARD
  },
  {
    id: 'dart',
    name: { en: 'Dart', ar: 'رأس سهم' },
    // Concave kite — validates that non-convex shapes work too.
    points: [{ x: 0, y: -160 }, { x: 90, y: 90 }, { x: 0, y: 20 }, { x: -90, y: 90 }],
    axisCount: 1, axisAngles: [90], difficulty: D.HARD
  },

  // ── More two-axis shapes ──────────────────────────────────────────
  {
    id: 'letter-h',
    name: { en: 'Letter H', ar: 'حرف H' },
    points: [
      { x: -140, y: -150 }, { x: -70, y: -150 }, { x: -70, y: -35 }, { x: 70, y: -35 },
      { x: 70, y: -150 }, { x: 140, y: -150 }, { x: 140, y: 150 }, { x: 70, y: 150 },
      { x: 70, y: 35 }, { x: -70, y: 35 }, { x: -70, y: 150 }, { x: -140, y: 150 }
    ],
    axisCount: 2, axisAngles: [0, 90], difficulty: D.MEDIUM
  },

  // ── More many-axis shapes ─────────────────────────────────────────
  {
    id: 'six-star',
    name: { en: 'Six-Point Star', ar: 'نجمة سداسية' },
    points: starPolygon(6, 165, 92),
    axisCount: 6, axisAngles: regularAxes(6), difficulty: D.EASY
  },
  {
    id: 'heptagon',
    name: { en: 'Heptagon', ar: 'سباعي' },
    points: regularPolygon(7, 160),
    axisCount: 7, axisAngles: regularAxes(7), difficulty: D.EASY
  },
  {
    id: 'octagon',
    name: { en: 'Octagon', ar: 'ثماني' },
    points: regularPolygon(8, 160, -67.5), // flat top → upright octagon
    axisCount: 8, axisAngles: regularAxes(8), difficulty: D.EASY
  }
];

/**
 * Build the round list for one session. The bank now holds many
 * one-axis shapes, so a naive "take the first N hardest" would serve an
 * all-hard session. Instead we sort into a hard→easy gradient (shuffled
 * within each axis-count band for freshness) and then sample `count`
 * shapes spread evenly across that gradient — every session ramps from
 * a tricky single-axis shape up to a friendly many-axis one, with a
 * different mix each time.
 */
export function buildSymmetryRounds(count: number): SymmetryShape[] {
  const gradient = shuffleBands([...SYMMETRY_SHAPES]);
  const n = gradient.length;
  const k = Math.min(count, n);
  if (k <= 0) return [];
  if (k === 1) return [gradient[0]];

  const used = new Set<number>();
  const out: SymmetryShape[] = [];
  for (let i = 0; i < k; i++) {
    let idx = Math.round((i * (n - 1)) / (k - 1));
    while (used.has(idx)) idx = (idx + 1) % n; // dedupe on rounding collisions
    used.add(idx);
    out.push(gradient[idx]);
  }
  return out;
}

/** Sort into a hard→easy (ascending axis-count) gradient, shuffling
 *  within each band so the specific shapes vary between sessions. */
function shuffleBands(shapes: SymmetryShape[]): SymmetryShape[] {
  const bands = new Map<number, SymmetryShape[]>();
  for (const s of shapes) {
    const band = bands.get(s.axisCount) ?? [];
    band.push(s);
    bands.set(s.axisCount, band);
  }
  const out: SymmetryShape[] = [];
  [...bands.keys()].sort((a, b) => a - b).forEach(k => {
    const band = bands.get(k)!;
    for (let i = band.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [band[i], band[j]] = [band[j], band[i]];
    }
    out.push(...band);
  });
  return out;
}
